// ---------------------------------------------------------------------------
// auto-save-flow-validation — deep validation for hand-edited flow saves (F6).
//
// The auto-save bridge's flowSteps subscription is the SINGLE flow
// persistence path, and until F6 it persisted hand edits with ZERO
// validation. validateFlowEditAndPublish runs the pure browser validator
// (@cogo/validator/browser validateActionFlow) over the EXACT payload that
// was just queued for save and publishes a flow-scoped verdict under the
// 'actionflow-edit' source in the canonical verdict store.
//
// Contract:
//   - NEVER gates or delays persistence — callers fire-and-forget AFTER
//     queueSave. D-406's fail-closed rule governs generation/flywheel
//     admission, not user-save persistence; blocking here would lose work.
//   - NEVER throws/rejects — the entire body is caught; on engine failure
//     nothing is published (the previous verdict, if any, stays) and a
//     single console.warn is emitted.
//   - Pure exported function (not a bridge closure) so persisted-op tools
//     (rename/delete flows) can reuse it.
//   - Run-token guarded: rapid edits launch overlapping async runs; only
//     the LATEST run may publish, so a slow stale result can never clobber
//     a newer verdict.
//   - The validator package is loaded via dynamic import (precedent:
//     services/project-api.ts) so the eagerly-loaded bridge chunk stays
//     small — do NOT "simplify" to a static import.
// ---------------------------------------------------------------------------

import type { TripleJson, ValidationReport } from '@cogo/validator/browser'
import {
  useValidationVerdictStore,
  type CanonicalVerdict,
} from '@/features/validation/verdict-store'

export interface FlowEditValidationInput {
  /** Owner block providing the ui/events/variables/datasource bundle context. */
  ownerBlock: Record<string, unknown> | undefined
  ownerBlockName: string
  /** Key the owner block declares the flow under (becomes the verdict scope). */
  flowKey: string
  /** The exact block-flow payload queueSave just persisted. */
  updatedFlow: Record<string, unknown>
}

/** Inline-JSON BlockBundle predicate (same IRI the jsonb parity cases use). */
const BUNDLE_AS_IRI = 'https://cogo.xyz/ontology/v1#bundleAs'
const SOURCE = 'actionflow-edit' as const
const LABEL = 'ActionFlow validation'

let currentRunToken = 0
let warnedOnce = false

// Single-flight loader for the validator package: rapid edits launch
// overlapping runs, and each must NOT issue its own import() (concurrent
// dynamic imports of the same module are wasteful in prod and race the
// module mocker under vitest). The first run performs the import; every
// later run awaits the same cached promise.
let validatorModule: Promise<typeof import('@cogo/validator/browser')> | null = null
function loadValidator() {
  validatorModule ??= import('@cogo/validator/browser').catch((err) => {
    // A failed chunk load must not poison every future validation — drop the
    // cached rejection so the next edit retries the import.
    validatorModule = null
    throw err
  })
  return validatorModule
}

/**
 * Wrap the flow-under-save plus its owner block's slots as ONE
 * `cogo:bundleAs` literal triple — the minimum-viable input codec
 * `validateActionFlow` reconstructs a BlockBundle from. Slot fallbacks
 * mirror project-api's block-bundle construction (manifest/meta → block,
 * state → variables, data → datasource).
 */
function buildBundleTriple(input: FlowEditValidationInput): TripleJson {
  const block = input.ownerBlock ?? {}
  const bundle = {
    block: block.manifest ?? block.meta ?? {},
    ui: block.ui ?? {},
    events: block.events ?? {},
    flow: input.updatedFlow,
    variables: block.variables ?? block.state ?? {},
    datasource: block.datasource ?? block.data ?? {},
  }
  return {
    s: `https://cogo.xyz/instance/${encodeURIComponent(input.ownerBlockName)}`,
    p: BUNDLE_AS_IRI,
    o: JSON.stringify(bundle),
    o_type: 'literal',
  }
}

function reportToVerdict(report: ValidationReport): CanonicalVerdict {
  if (report.summary.errors > 0) return 'fix'
  if (report.summary.warnings > 0) return 'review'
  return 'ok'
}

/** Headline of the worst issue: first error, else first warning. */
function worstCheckOf(report: ValidationReport): string | null {
  const issues = report.stages.flatMap((stage) => stage.issues)
  const worst =
    issues.find((issue) => issue.level === 'error') ??
    issues.find((issue) => issue.level === 'warning')
  return worst ? worst.message : null
}

/**
 * Validate a just-persisted hand-edited flow and publish the result as a
 * flow-scoped 'actionflow-edit' verdict. See module header for the contract
 * (never gates saves, never throws, latest-run-wins).
 */
export async function validateFlowEditAndPublish(
  input: FlowEditValidationInput,
): Promise<void> {
  const token = ++currentRunToken
  try {
    const startedAt = Date.now()
    const { validateActionFlow } = await loadValidator()
    const report = await validateActionFlow({
      vocabulary: [],
      instance: [buildBundleTriple(input)],
    })
    // A newer edit launched a newer run while this one was in flight — this
    // result is stale and must not clobber the newer verdict.
    if (token !== currentRunToken) return

    useValidationVerdictStore.getState().publish({
      source: SOURCE,
      label: LABEL,
      verdict: reportToVerdict(report),
      score: report.summary.score,
      // The validator reports issue counts, not a per-check pass count.
      counts: { pass: 0, warn: report.summary.warnings, fail: report.summary.errors },
      worstCheck: worstCheckOf(report),
      startedAt,
      // Scoped: deriveAppHealth's honesty rule keeps a flow-scoped 'ok' from
      // faking app-level green while a flow-scoped 'fix' still escalates.
      scope: `Flow: ${input.flowKey}`,
    })
  } catch (err) {
    // Publish nothing — the save already went through and must stay
    // unaffected; an engine failure is not evidence about the flow.
    if (!warnedOnce) {
      warnedOnce = true
      console.warn(
        '[auto-save-flow-validation] flow-edit validation failed (save unaffected):',
        err,
      )
    }
  }
}
